import { supabase } from '../../services/supabase';
import { SHOUTOUT_INBOX_REFRESH_EVENT, uploadShoutOutImage, type ShoutOutAttachment } from './useShoutOutInbox';

/**
 * "Message ShoutOut" — reach a person on the team without going through any AI
 * assistant. It opens a support thread through `creator_support_handoff`, the
 * same function the assistant will call, with the creator's own message as the
 * whole transcript. When the assistant exists it goes in front of this; the
 * thread shape and the staff queue stay the same.
 *
 * Any surface opens the sheet by dispatching OPEN_EVENT (the Inbox button, the
 * bottom-left Support launcher once Creator Chat is retired); the sheet is
 * mounted once in the workspace shell.
 */

export const MESSAGE_SHOUTOUT_OPEN_EVENT = 'message-shoutout:open';

export function openMessageShoutOut(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MESSAGE_SHOUTOUT_OPEN_EVENT));
}

export function refreshShoutOutInbox(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SHOUTOUT_INBOX_REFRESH_EVENT));
}

export type SupportRequestResult = {
  threadId: string;
  /** The message went through but the photo did not; the creator must re-add it. */
  photoFailed: boolean;
};

/**
 * Open a support thread with the creator's message, then attach the photo (if
 * any) as a follow-up in the same thread.
 *
 * The photo is uploaded only AFTER the thread exists, so a failed request never
 * leaves an orphaned file in storage. A photo that fails after that is reported
 * back, not swallowed: the message has reached the team, and the creator is
 * told to add the photo again in the conversation.
 */
export async function sendSupportRequest(text: string, photo: File | null): Promise<SupportRequestResult> {
  const body = text.trim();
  if (!body) throw new Error('Write a message first.');

  const { data, error } = await supabase.rpc('creator_support_handoff', {
    p_transcript: [{ role: 'creator', content: body }],
  });
  if (error) throw new Error(error.message);
  const threadId = data as string;

  let photoFailed = false;
  if (photo) {
    try {
      const attachment: ShoutOutAttachment = await uploadShoutOutImage(photo);
      const { error: photoError } = await supabase.rpc('creator_reply', {
        p_thread_id: threadId,
        p_body: '',
        p_attachments: [attachment],
      });
      if (photoError) throw photoError;
    } catch (e: any) {
      console.warn('Support photo could not be attached:', e?.message || e);
      photoFailed = true;
    }
  }

  refreshShoutOutInbox();
  return { threadId, photoFailed };
}
